import {useCallback, useEffect, useRef, useState} from 'react';
import {Animated, Easing} from 'react-native';

/**
 * Drives a panel/palette that:
 *   - opens when a "ready" signal transitions false -> true (e.g. user lands),
 *   - auto-collapses ONCE after a delay following that first auto-open,
 *   - can be toggled open/closed imperatively.
 *
 * Auto-collapse only applies to the initial auto-open. After the user manually
 * expands the panel it stays open until they collapse it themselves — we never
 * auto-collapse a user-initiated open.
 *
 * Returns the open state, open/close helpers, and an Animated progress value
 * (0 = collapsed, 1 = open) so callers can drive their own slide/fade.
 *
 * This is the same interaction as the top-left Unit detail box
 * (components/World/UnitHeaderOverlay), extracted so it can be reused.
 *
 * @param {object}  options
 * @param {boolean} options.reopenSignal    Re-opens on a false -> true edge.
 * @param {number}  options.autoCollapseMs  Delay before auto-collapsing.
 * @param {boolean} options.initialOpen     Initial open state.
 */
const useAutoCollapsePanel = ({
  reopenSignal = false,
  autoCollapseMs = 5000,
  initialOpen = true,
} = {}) => {
  const [isOpen, setIsOpen] = useState(initialOpen);
  const openProgress = useRef(new Animated.Value(initialOpen ? 1 : 0)).current;
  const collapseTimerRef = useRef(null);
  const prevReopenSignalRef = useRef(reopenSignal);
  // Auto-collapse is a one-shot: armed until the initial auto-open collapses, or
  // until the user manually expands the panel — after which they close it
  // themselves and we never auto-collapse again.
  const autoCollapseArmedRef = useRef(true);

  // Animate progress whenever the open state flips.
  useEffect(() => {
    Animated.timing(openProgress, {
      toValue: isOpen ? 1 : 0,
      duration: isOpen ? 280 : 340,
      easing: isOpen
        ? Easing.out(Easing.cubic)
        : Easing.bezier(0.22, 1, 0.36, 1),
      useNativeDriver: true,
    }).start();
  }, [isOpen, openProgress]);

  // Re-open on the ready signal's rising edge.
  useEffect(() => {
    if (!prevReopenSignalRef.current && reopenSignal) {
      setIsOpen(true);
    }
    prevReopenSignalRef.current = reopenSignal;
  }, [reopenSignal]);

  // Auto-collapse after the delay — but only while still armed (the first
  // auto-open). A manual expand disarms it, so it never re-collapses on its own.
  useEffect(() => {
    if (!isOpen || !autoCollapseArmedRef.current) {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
      return undefined;
    }

    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
    }
    collapseTimerRef.current = setTimeout(() => {
      autoCollapseArmedRef.current = false;
      setIsOpen(false);
    }, autoCollapseMs);

    return () => {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
    };
  }, [isOpen, autoCollapseMs]);

  const open = useCallback(() => {
    // User-initiated expand: opt out of auto-collapse from here on.
    autoCollapseArmedRef.current = false;
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);

  return {isOpen, open, close, openProgress};
};

export default useAutoCollapsePanel;
